import { useStoreState } from '@ariakit/react';
import { Popover, PopoverDisclosure, PopoverHeading, usePopoverStore } from '@ariakit/react/popover';
import { createColumnHelper } from '@tanstack/react-table';
import cn from 'classnames';
import { Link } from '#components/atoms/link/Link.tsx';
import { Table } from '#components/patterns/table/Table.tsx';
import type { EndpointOIDCUser } from '#dn-types/endpoint-oidc-user.ts';
import type { EndpointOIDC } from '#dn-types/endpoint-oidc.ts';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { ISSUER_URL_FILTER_PARAM } from '#screens/settings/sso/list/useIssuerURLFilter.ts';
import { useSuspenseAllOIDCProvidersQuery } from '#screens/settings/sso/sso-page/SSOSettingsPage.queries.ts';
import { useHostCountsByUser } from './EndpointOIDCUsersTable.queries.ts';
import styles from './EndpointOIDCUsersTable.module.css';

type Props = {
  data: EndpointOIDCUser[];
};

/** Undefined until the counts load, or if they fail: the cell then links without claiming a count. */
type Row = EndpointOIDCUser & { hostCount: number | undefined };

const columnHelper = createColumnHelper<Row>();

const columns = [
  columnHelper.accessor('email', {
    header: 'Email',
    size: 280,
    meta: { truncate: true },
    cell(info) {
      const email = info.getValue();
      const { subject } = info.row.original;

      // Email isn't unique per user, issuer + subject is, so the subject stays visible to tell apart
      // two users who share an email.
      return (
        <div className="flex flex-col">
          <span className="truncate" title={email}>
            {email}
          </span>
          <span className="p-sm mb-0 truncate" title={`Subject: ${subject}`}>
            <span className="visually-hidden">Subject: </span>
            {subject}
          </span>
        </div>
      );
    },
  }),
  columnHelper.accessor('issuerURL', {
    header: 'Provider',
    size: 230,
    meta: { truncate: true },
    cell(info) {
      return <ProviderCell issuerURL={info.getValue()} />;
    },
  }),
  columnHelper.accessor('hostCount', {
    header: 'Hosts',
    size: 90,
    cell(info) {
      const hostCount = info.getValue();
      const { id, email } = info.row.original;
      const label = hostCount === undefined ? 'View' : `${hostCount} ${hostCount === 1 ? 'host' : 'hosts'}`;
      return (
        <Link
          className="a"
          to={`/hosts?filter.endpointOIDCUserID=${encodeURIComponent(id)}`}
          aria-label={`${hostCount === undefined ? 'View hosts' : label} for ${email}`}
        >
          {label}
        </Link>
      );
    },
  }),
];

const PROVIDER_NAMES_SHOWN = 2;

/**
 * Lists every endpoint provider that accepts a user's issuer, not the one they signed in through:
 * providers don't have to keep issuers unique (Google issues one URL for every app), so only the user's
 * hosts could say which provider enrolled them. Deleting a provider or editing its issuer URL leaves its
 * users behind on an issuer no provider accepts, and they can't re-authenticate.
 */
function ProviderCell({ issuerURL }: { issuerURL: string }) {
  const providers = useSuspenseAllOIDCProvidersQuery()
    .data.endpoint.data.filter((p) => p.issuerURL === issuerURL)
    // Enabled first, so the likeliest sign-in path leads
    .toSorted(
      (a, b) => Number(b.enabled) - Number(a.enabled) || a.name.localeCompare(b.name, undefined, { numeric: true })
    );
  const shown = providers.slice(0, PROVIDER_NAMES_SHOWN);
  const extraCount = providers.length - shown.length;

  return (
    <div className="flex flex-col">
      {providers.length === 0 ?
        <span className={styles.EndpointOIDCUsersTable_lowlight}>No matching provider</span>
      : <span className="flex gap-4 align-center mw-0">
          <span className="truncate" title={shown.map((p) => (p.enabled ? p.name : `${p.name} (disabled)`)).join(', ')}>
            {shown.map((p, i) => (
              <span key={p.id}>
                {i > 0 ? ', ' : null}
                <ProviderLink provider={p} />
              </span>
            ))}
          </span>
          {extraCount > 0 ?
            <ProvidersPopover issuerURL={issuerURL} providers={providers} label={`+${extraCount}`} />
          : null}
        </span>
      }
      <span className="p-sm mb-0 truncate" title={issuerURL}>
        {issuerURL}
      </span>
    </div>
  );
}

function ProviderLink({ provider, onClick }: { provider: EndpointOIDC; onClick?: () => void }) {
  const location = useAppLocation();

  return (
    <>
      <Link
        className="a inline truncate"
        to={`/settings/sso/${provider.id}`}
        state={{ from: location }}
        title={provider.name}
        onClick={onClick}
      >
        {provider.name}
      </Link>
      {provider.enabled ? null : <span className={styles.EndpointOIDCUsersTable_lowlight}> (disabled)</span>}
    </>
  );
}

type ProvidersPopoverProps = {
  issuerURL: string;
  providers: EndpointOIDC[];
  label: string;
};

/** Every provider sharing the issuer, each linked, plus the SSO tab filtered to the issuer; the cell already shows the issuer. */
function ProvidersPopover({ issuerURL, providers, label }: ProvidersPopoverProps) {
  const popover = usePopoverStore({ placement: 'bottom-start' });
  const isOpen = useStoreState(popover, 'open');

  return (
    <>
      <PopoverDisclosure
        store={popover}
        className={cn(styles.EndpointOIDCUsersTable_trigger, { [styles.EndpointOIDCUsersTable_triggerOpen]: isOpen })}
        aria-label={`Show all ${providers.length} providers for ${issuerURL}`}
      >
        {label}
      </PopoverDisclosure>
      <Popover store={popover} portal={true} gutter={6} className={styles.EndpointOIDCUsersTable_popover}>
        {isOpen ?
          <>
            <PopoverHeading
              className={cn('eyebrow', styles.EndpointOIDCUsersTable_popoverHeader)}
              render={<h3>{providers.length} providers use this issuer</h3>}
            />
            <ul className={cn('flex flex-col gap-8', styles.EndpointOIDCUsersTable_popoverList)}>
              {providers.map((p) => (
                <li className="mw-0" key={p.id}>
                  <ProviderLink provider={p} onClick={popover.hide} />
                </li>
              ))}
            </ul>
            <Link
              className={cn('a inline', styles.EndpointOIDCUsersTable_popoverFooter)}
              to={`/settings/sso?tab=endpoint&${ISSUER_URL_FILTER_PARAM}=${encodeURIComponent(issuerURL)}`}
              onClick={popover.hide}
            >
              View all {providers.length} in SSO settings
            </Link>
          </>
        : null}
      </Popover>
    </>
  );
}

export function EndpointOIDCUsersTable({ data }: Props) {
  const hostCounts = useHostCountsByUser();
  const rows = data.map(
    (user): Row => ({
      ...user,
      hostCount: hostCounts.data === undefined ? undefined : (hostCounts.data.get(user.id) ?? 0),
    })
  );

  return <Table data={rows} columns={columns} fixedLayout={true} caption="Endpoint OIDC users" />;
}
